import {
    Table,
    TableBody,
    TableContainer,
    useTheme,
    Card,
    Stack,
    Typography,
    Link as MuiLink
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Chip, { chipColors } from '@/components/shared/Chip';
import {
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableHeaderCellWithCheckbox,
    TableRow,
    TableSortLabel
} from '@/components/shared/styles/Table';
import { TablePagination } from '@/components/shared/TablePagination';
import CardHeader from '@/components/shared/CardHeader';
import { SMBold } from '@/typography/Typography';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '@/lib/constants';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { SortDirection } from '@/lib/hooks/useAPIOrdering';

export type SignedDocument = {
    id: string;
    name: string;
    email: string;
    signedDate: string;
    documentName: string;
    documentUrl?: string;
};

export type InstaSignTableProps = {
    documents: SignedDocument[];
    total: number;
    filteredTotal: number;
    rowsPerPage: number;
    handleChangeRowsPerPage?: (rowsPerPage: number) => void;
    onDownloadDocument?: (documentId: string) => void;
    sortField: string;
    sortDirection: SortDirection;
    onRequestSort: (field: string) => void;
    orderMap: Record<string, SortDirection>;
};

export const InstaSignTable = ({
    documents,
    total,
    filteredTotal,
    rowsPerPage,
    handleChangeRowsPerPage,
    onDownloadDocument,
    sortField,
    sortDirection,
    onRequestSort,
    orderMap
}: InstaSignTableProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const handleDownload = (document: SignedDocument) => {
        if (onDownloadDocument) {
            onDownloadDocument(document.id);
        }
    };

    const createSortHandler = (field: string) => () => {
        onRequestSort(field);
    };

    const actions = <Stack direction="row"></Stack>;

    return (
        <Card>
            <CardHeader
                label={t('signed_documents')}
                chip={
                    total ? (
                        <Chip
                            icon={<span></span>}
                            label={
                                <SMBold color={chipColors.success.text}>
                                    {filteredTotal}
                                </SMBold>
                            }
                            color={'success'}
                        />
                    ) : (
                        <></>
                    )
                }
                actions={actions}
            />
            <TableContainer style={{ minWidth: '1424px' }}>
                <Table sx={{}} aria-label="instasign table">
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>
                                <TableSortLabel
                                    active={sortField === 'employeeName'}
                                    direction={
                                        orderMap['employeeName'] || 'asc'
                                    }
                                    onClick={createSortHandler('employeeName')}
                                >
                                    {t('name')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <TableSortLabel
                                    active={sortField === 'employeeEmail'}
                                    direction={
                                        orderMap['employeeEmail'] || 'asc'
                                    }
                                    onClick={createSortHandler('employeeEmail')}
                                >
                                    {t('email')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <TableSortLabel
                                    active={sortField === 'signedAt'}
                                    direction={orderMap['signedAt'] || 'asc'}
                                    onClick={createSortHandler('signedAt')}
                                >
                                    {t('signed_date')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <TableSortLabel
                                    active={sortField === 'documentName'}
                                    direction={
                                        orderMap['documentName'] || 'asc'
                                    }
                                    onClick={createSortHandler('documentName')}
                                >
                                    {t('document_name')}
                                </TableSortLabel>
                            </TableHeaderCell>
                            <TableHeaderCell></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {documents?.map((document) => {
                            return (
                                <TableRow key={document.id}>
                                    <>
                                        <TableCell component="th" scope="row">
                                            {document.name}
                                        </TableCell>
                                        <TableCell>{document.email}</TableCell>
                                        <TableCell>
                                            {dayjs(document.signedDate).format(
                                                DATE_FORMAT.DAY_NAME_MONTH_NAME_DAY_YEAR
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <MuiLink
                                                href={
                                                    document.documentUrl || '#'
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{
                                                    color: theme.palette.primary
                                                        .main,
                                                    textDecoration: 'none',
                                                    '&:hover': {
                                                        textDecoration:
                                                            'underline'
                                                    }
                                                }}
                                            >
                                                {document.documentName}
                                            </MuiLink>
                                        </TableCell>
                                        <TableCell align="right">
                                            <div
                                                style={{
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    alignItems: 'center'
                                                }}
                                                onClick={() =>
                                                    handleDownload(document)
                                                }
                                            >
                                                <FileDownloadOutlinedIcon />
                                            </div>
                                        </TableCell>
                                    </>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                {documents.length == 0 && (
                    <Typography
                        style={{
                            padding: '20px'
                        }}
                        textAlign={'center'}
                    >
                        {t('no_signed_documents_found')}
                    </Typography>
                )}
            </TableContainer>
            <TablePagination
                count={filteredTotal!}
                page={0}
                onPageChange={() => {}}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage as any}
                rowsPerPageOptions={[
                    {
                        label: '16',
                        value: 16
                    },
                    {
                        label: '24',
                        value: 24
                    },
                    {
                        label: '32',
                        value: 32
                    },
                    {
                        label: 'All',
                        value: -1
                    }
                ]}
            />
        </Card>
    );
};
